import { date } from 'quasar'
import { imgixHost, isAdmin } from 'src/config'
import Activity from 'src/models/activity/Activity'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import ConfigurationAudit from 'src/models/configuration-audit/ConfigurationAudit'
import Item from 'src/models/item/Item'
import ItemClass from 'src/models/item/ItemClass'
import ItemVariation from 'src/models/item/ItemVariation'
import MembershipType from 'src/models/membership-type/MembershipType'
import { MEMBERSHIP_VOUCHER_CONDITIONS } from 'src/models/membership-type/MembershipVoucher'
import Model, { encoders, ID, Integer, IsoDate, JsonObject, MODEL_LEVEL } from 'src/models/Model'
import Movie from 'src/models/movie/Movie'
import TitleClass from 'src/models/movie/TitleClass'
import Tax from 'src/models/order/Tax'
import { ORDER_ITEM_SUBTYPES, ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import ItemPackage from 'src/models/package/ItemPackage'
import Showing from 'src/models/showing/Showing'
import TicketType from 'src/models/showing/TicketType'
import Site from 'src/models/site/Site'

const { addToDate } = date

export const VOUCHER_TYPE_TYPES = {
  ITEM: 'item',
  ITEM_PACKAGE: 'item-package',
  SPECIFIC_ITEM: 'specific-item',
  SPECIFIC_MEMBERSHIP: 'specific-membership',
  TICKET: 'ticket',
  ACTIVITY_TICKET: 'activity-ticket'
}
VOUCHER_TYPE_TYPES.ALL = _.values(VOUCHER_TYPE_TYPES)
VOUCHER_TYPE_TYPES.SPECIAL = [VOUCHER_TYPE_TYPES.ITEM_PACKAGE, VOUCHER_TYPE_TYPES.SPECIFIC_ITEM, VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP]

export const VOUCHER_TYPE_EXPIRY_TERMS = {
  MONTHS: 'months',
  YEARS: 'years'
}
VOUCHER_TYPE_EXPIRY_TERMS.ALL = _.values(VOUCHER_TYPE_EXPIRY_TERMS)

export const VOUCHER_TYPE_CONDITIONS = {
  DATE_RANGE: 'date-range',
  DATES: 'dates',
  DAY_OF_WEEK: 'day-of-week',
  SITES: 'sites',
  TIME_OF_DAY: 'time-of-day'
}
VOUCHER_TYPE_CONDITIONS.ALL = _.values(VOUCHER_TYPE_CONDITIONS)

export const CONDITION_REQUIREMENTS = {
  // DATE_RANGE
  DATE_RANGE_START: 'dateRangeStart',
  DATE_RANGE_END: 'dateRangeEnd',
  EXCLUDE_DATE_RANGE: 'excludeDateRange',
  // DAY_OF_WEEK
  DAY_OF_WEEK: 'dayOfWeek',
  EXCLUDE_DAY_OF_WEEK: 'excludeDayOfWeek',
  // DATES
  DATES: 'dates',
  EXCLUDE_DATES: 'excludeDates',
  // SITES
  SITES: 'siteIds',
  EXCLUDE_SITES: 'excludeSites',
  // TIME_OF_DAY
  TIME_OF_DAY_START: 'timeOfDayStart',
  TIME_OF_DAY_END: 'timeOfDayEnd',
  EXCLUDE_TIME_OF_DAY: 'excludeTimeOfDay'
}
CONDITION_REQUIREMENTS.ALL = _.values(CONDITION_REQUIREMENTS)

export default class VoucherType extends Model {
  static name = 'VoucherType'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static hasAuditSummary = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Promotions]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      type: {
        type: String,
        options: VOUCHER_TYPE_TYPES.ALL,
        default: VOUCHER_TYPE_TYPES.TICKET
      },
      description: {
        type: String,
        default: null,
        translatable: true
      },
      image: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      },
      sites: {
        type: [Site],
        reference: true
      },
      itemClasses: {
        type: [ItemClass],
        reference: true
      },
      excludeItemClasses: {
        type: Boolean,
        default: false
      },
      items: {
        type: [Item],
        reference: true
      },
      siteItems: {
        type: [Item],
        reference: true
      },
      excludeItems: {
        type: Boolean,
        default: false
      },
      itemVariations: {
        type: [ItemVariation],
        reference: true
      },
      siteItemVariations: {
        type: [ItemVariation],
        reference: true
      },
      excludeItemVariations: {
        type: Boolean,
        default: false
      },
      ticketTypes: {
        type: [TicketType],
        reference: true
      },
      siteTicketTypes: {
        type: [TicketType],
        reference: true
      },
      excludeTicketTypes: {
        type: Boolean,
        default: false
      },
      titleClasses: {
        type: [TitleClass],
        reference: true
      },
      excludeTitleClasses: {
        type: Boolean,
        default: false
      },
      movies: {
        type: [Movie]
      },
      movieIds: {
        type: [ID],
        default: () => ([])
      },
      siteMovies: {
        type: [Movie],
        output: true
      },
      siteMovieIds: {
        type: [ID],
        default: () => ([])
      },
      excludeMovies: {
        type: Boolean,
        default: false
      },
      showings: {
        type: [Showing]
      },
      showingIds: {
        type: [ID],
        default: () => ([])
      },
      siteShowings: {
        type: [Showing],
        output: true
      },
      siteShowingIds: {
        type: [ID],
        default: () => ([])
      },
      excludeShowings: {
        type: Boolean,
        default: false
      },
      activities: {
        type: [Activity]
      },
      activityIds: {
        type: [ID],
        default: () => ([])
      },
      siteActivities: {
        type: [Activity],
        output: true
      },
      siteActivityIds: {
        type: [ID],
        default: () => ([])
      },
      excludeActivities: {
        type: Boolean,
        default: false
      },
      activitySessions: {
        type: [ActivitySession]
      },
      activitySessionIds: {
        type: [ID],
        default: () => ([])
      },
      siteActivitySessions: {
        type: [ActivitySession],
        output: true
      },
      siteActivitySessionIds: {
        type: [ID],
        default: () => ([])
      },
      excludeActivitySessions: {
        type: Boolean,
        default: false
      },
      forSale: {
        type: Boolean,
        default: false
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      serviceIssue: {
        type: Boolean,
        default: false
      },
      membershipType: {
        type: MembershipType,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      membershipTerm: {
        type: Integer,
        default: 12
      },
      itemVariation: {
        type: ItemVariation,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      itemPackage: {
        type: ItemPackage,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      membershipTypes: {
        type: [MembershipType],
        default: () => ([]),
        reference: true
      },
      membershipTypeRestrictionsEnabled: {
        type: Boolean,
        default: false
      },
      membershipTypeRestrictionsExclude: {
        type: Boolean,
        default: false
      },
      hasMaxPrice: {
        type: Boolean,
        default: false
      },
      maxPrice: {
        type: Number,
        encode: encoders.cents,
        default: 10
      },
      hasMinPrice: {
        type: Boolean,
        default: false
      },
      minPrice: {
        type: Number,
        encode: encoders.cents,
        default: 10
      },
      taxable: {
        type: Boolean,
        default: false
      },
      taxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      expiryOptions: {
        type: JsonObject,
        default: () => ({
          has_expiry: false,
          expiry_value: 1,
          expiry_term: 'years'
        })
      },
      isReward: {
        type: Boolean,
        default: false
      },
      isCeaCard: {
        type: Boolean,
        default: false
      },
      pointsCost: {
        type: Integer,
        default: 100
      },
      hasMaxReportedPrice: {
        type: Boolean,
        default: false
      },
      maxReportedPrice: {
        type: Number,
        encode: encoders.cents,
        default: 10
      },
      hasMinReportedPrice: {
        type: Boolean,
        default: false
      },
      minReportedPrice: {
        type: Number,
        encode: encoders.cents,
        default: 10
      },
      hasCustomReportingName: {
        type: Boolean,
        default: false
      },
      customReportingName: {
        type: String,
        default: null
      },
      limitRedemptionsByShowingEnabled: {
        type: Boolean,
        default: false
      },
      limitRedemptionsByShowingIncludesOverlapsEnabled: {
        type: Boolean,
        default: false
      },
      showingRedemptionsLimit: {
        type: Number,
        default: 1
      },
      limitRedemptionsByActivitySessionEnabled: {
        type: Boolean,
        default: false
      },
      limitRedemptionsByActivitySessionAdmissionTimes: {
        type: Boolean,
        default: true
      },
      activitySessionRedemptionsLimit: {
        type: Number,
        default: 1
      },
      conditions: {
        type: JsonObject,
        default: _(VOUCHER_TYPE_CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          dateRangeStart: null,
          dateRangeEnd: null,
          excludeDateRange: false,
          dates: [],
          excludeDates: false,
          dayOfWeek: [],
          excludeDayOfWeek: false,
          excludeSites: false,
          timeOfDayStart: null,
          timeOfDayEnd: null,
          excludeTimeOfDay: false
        })
      },
      barcodeOptions: {
        type: JsonObject,
        default: () => ({
          require_assigned_card: false,
          require_card_uid_prefix_match: false,
          card_uid_prefix: '',
          card_uid_increment_length: 0,
          card_uid_length: 10
        })
      },
      modelAudits: {
        type: [ConfigurationAudit],
        default: () => { return [] },
        output: true,
        excludeFromDefault: !isAdmin
      },
      forSelf: {
        type: Boolean,
        local: true
      },
      userId: {
        type: ID,
        local: true
      }
    }

    this.computed = {
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?fit=crop&w=600&h=400&fill=solid&fill-color=${this.color}&bg=${this.color}&fm=jpeg&auto=format`
      }
    }
  }

  get label () {
    return this.name
  }

  validateVoucher (orderItem, voucher = null) {
    if (orderItem.subtotal <= 0) return false

    const isSpecialVoucherTypeType = _.includes(VOUCHER_TYPE_TYPES.SPECIAL, this.type)
    if (orderItem.type !== this.type && !isSpecialVoucherTypeType) return false

    if ((this.type === VOUCHER_TYPE_TYPES.ITEM_PACKAGE) && !orderItem.itemPackage) return false

    if (this.isCeaCard && (this.type !== VOUCHER_TYPE_TYPES.TICKET || orderItem.type !== ORDER_ITEM_TYPES.TICKET || !orderItem.ticket.ticketType.isCeaCard)) return false

    if (this.isCeaCard && !_.some(orderItem.order?.orderItems?.data, oi => oi.type === ORDER_ITEM_TYPES.TICKET && oi !== orderItem)) return false

    if (orderItem.order.purchasingMembership && this.type !== VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP) return false

    if (this.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM && (orderItem.type !== ORDER_ITEM_TYPES.ITEM || !this.itemVariation || this.itemVariation?.id !== orderItem.itemOrderItem?.itemVariation?.id)) return false

    if (this.type === VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP) {
      if (orderItem.type !== ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL || orderItem.subtype !== ORDER_ITEM_SUBTYPES.MEMBERSHIP_INTERVAL_NEW) return false

      if (orderItem.membershipInterval.term > this.membershipTerm) return false

      if (_.some(orderItem.details, detail => !!detail.user_data && detail.user_data.membership_type_id !== this.membershipTypeId)) return false
    }

    if (voucher?.forSelf && (!orderItem.ticket || !orderItem.ticket.user || orderItem.ticket.user.id !== voucher?.user.id)) return false

    if (!this.isSiteMatch(voucher)) return false

    // Walk through rules from general to specific. A specific match can override a more
    // general rule. We only set the implicit include / exclude for the first rule that matches (why we check for null)
    // This means the first, most general rule specified sets up the initial implicit exclude
    if ((this.type === VOUCHER_TYPE_TYPES.TICKET || this.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET) && (this.excludeTicketTypes || !_.isEmpty(this.ticketTypeIds))) {
      if (!!this.excludeTicketTypes === _.includes(this.ticketTypeIds, (orderItem.ticket?.ticketType?.id || orderItem.activityTicket?.ticketType?.id))) return false
    } else if (this.type === VOUCHER_TYPE_TYPES.ITEM) {
      if (!this.isItemOrderItem(orderItem.itemOrderItem)) return false
    }

    if (orderItem.localShowing) {
      if (!this.isShowingMatch(orderItem.localShowing)) return false
    }

    if (orderItem.localActivityTicket) {
      if (!this.isActivitySessionMatch(orderItem.localActivityTicket.activitySession)) return false
    }

    if (orderItem.localActivityTicket) {
      if (!this.isActivitySessionMatch(orderItem.localActivityTicket.activitySession)) return false
    }

    if (!this.isDateMatch(orderItem, voucher)) return false

    if (voucher?.validShowingDate && (!orderItem.ticket || date.formatDate(voucher?.validShowingDate, 'YYYY-MM-DD') !== date.formatDate(orderItem.ticket.showing.time, 'YYYY-MM-DD'))) return false

    if (this.type !== VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP && this.limitRedemptionsByShowingEnabled) {
      const orderItemShowing = orderItem.localShowing
      if (orderItemShowing) {
        const voucherRedemptionsByShowing = (
          voucher?.user
            ? (voucher?.user.redemptionsByShowing.voucher_types[this.id] ?? {})
            : orderItem.order.anonymousVoucherRedemptions[this.id]?.byShowing
        )
        if (voucherRedemptionsByShowing) {
          const showingVoucherRedemptionsCount = (
            voucher?.user
              ? (voucherRedemptionsByShowing[orderItemShowing.id]?.count ?? 0) + (orderItem.order.userVoucherRedemptions[voucher?.user.id]?.[this.id]?.byShowing[orderItemShowing.id]?.count ?? 0)
              : orderItem.order.anonymousVoucherRedemptions[this.id]?.byShowing[orderItemShowing.id]?.count ?? 0
          )
          if (showingVoucherRedemptionsCount >= this.showingRedemptionsLimit) return false
          if (this.limitRedemptionsByShowingIncludesOverlapsEnabled) {
            const overlappingVoucherRedemptions = _.filter(_.values(voucherRedemptionsByShowing), showing => {
              const showingStartTime = new Date(showing.showing_overlap_start_time)
              const showingEndTime = new Date(showing.showing_overlap_end_time)

              // Overlaps if local showing start time is within redemption showing time
              if (showingStartTime <= orderItemShowing.time && showingEndTime >= orderItemShowing.time) return true
              // Overlaps if local showing end time is within redemption showing time
              if (showingStartTime <= orderItemShowing.endTime && showingEndTime >= orderItemShowing.endTime) return true
              // Overlaps if local showing start time is before redemption showing start time and local showing end time is after redemption showing end time
              if (showingStartTime > orderItemShowing.time && showingEndTime < orderItemShowing.endTime) return true

              return false
            })
            const userOrAnonymousRedemptionCount = voucher?.user ? (orderItem.order.userVoucherRedemptions[voucher?.user.id]?.[this.id]?.count ?? 0) : orderItem.order.anonymousVoucherRedemptions[this.id] ?? 0
            const overlappingVoucherRedemptionsCount = _.sumBy(overlappingVoucherRedemptions, 'count') + userOrAnonymousRedemptionCount

            if (overlappingVoucherRedemptionsCount >= this.showingRedemptionsLimit) return false
          }
        }
      }
    }
    if (this.type !== VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP && this.limitRedemptionsByActivitySessionEnabled) {
      const activityTicket = orderItem.localActivityTicket

      if (activityTicket) {
        const existingVoucherRedemptionsForActivitySession = voucher?.user?.redemptionsByActivitySession.voucher_types[this.id]?.[activityTicket.activitySession.id]
        // eslint-disable-next-line camelcase
        const existingVoucherRedemptionsForActivitySessionCount = _(existingVoucherRedemptionsForActivitySession).map(({ entry_time, exit_time, count }) => {
          if (!this.limitRedemptionsByActivitySessionAdmissionTimes) {
            return count
          }

          const activitySessionEntryTime = new Date(entry_time)
          const activitySessionExitTime = new Date(exit_time)

          // Overlaps if local activity ticket start time is within redemption activity ticket time
          if (activitySessionEntryTime <= activityTicket.entryTime && activitySessionExitTime >= activityTicket.entryTime) return count
          // Overlaps if local activity ticket end time is within redemption activity ticket time
          if (activitySessionEntryTime <= activityTicket.exitTime && activitySessionExitTime >= activityTicket.exitTime) return count
          // Overlaps if local activity ticket start time is before redemption activity ticket start time and local activity ticket end time is after redemption activity ticket end time
          if (activitySessionEntryTime > activityTicket.entryTime && activitySessionExitTime < activityTicket.exitTime) return count

          return null
        }).compact().sum()

        const currentVoucherRedemptionsForActivitySession = voucher?.user
          ? orderItem.order.userVoucherRedemptions[voucher?.user.id][this.id].byActivitySession[activityTicket.activitySession.id]
          : orderItem.order.anonymousVoucherRedemptions[this.id].byActivitySession[activityTicket.activitySession.id]
        // eslint-disable-next-line camelcase
        const currentVoucherRedemptionsForActivitySessionCount = _(currentVoucherRedemptionsForActivitySession).map(({ entry_time, exit_time, count }) => {
          if (!this.limitRedemptionsByActivitySessionAdmissionTimes) {
            return count
          }

          const activitySessionEntryTime = new Date(entry_time)
          const activitySessionExitTime = new Date(exit_time)

          // Overlaps if local activity ticket start time is within redemption activity ticket time
          if (activitySessionEntryTime <= activityTicket.entryTime && activitySessionExitTime >= activityTicket.entryTime) return count
          // Overlaps if local activity ticket end time is within redemption activity ticket time
          if (activitySessionEntryTime <= activityTicket.exitTime && activitySessionExitTime >= activityTicket.exitTime) return count
          // Overlaps if local activity ticket start time is before redemption activity ticket start time and local activity ticket end time is after redemption activity ticket end time
          if (activitySessionEntryTime > activityTicket.entryTime && activitySessionExitTime < activityTicket.exitTime) return count

          return null
        }).compact().sum()

        const allVoucherRedemptionsForActivitySessionCount = existingVoucherRedemptionsForActivitySessionCount + currentVoucherRedemptionsForActivitySessionCount

        if (allVoucherRedemptionsForActivitySessionCount >= this.activitySessionRedemptionsLimit) {
          return false
        }
      }
    }
    return true // We found a match!
  }

  isSiteMatch (voucher = null) {
    const isSiteRestricted = !this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] || this.siteIds?.length
    if (this.conditions[VOUCHER_TYPE_CONDITIONS.SITES] && isSiteRestricted) {
      if (this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] === _.includes(this.siteIds, this.configuration.currentSite.id)) return false
    }

    if (!voucher?.membershipVoucher) return true

    const isMembershipVoucherSiteRestricted = !voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] || voucher.membershipVoucher.siteIds.length
    if (voucher.membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.SITES] && isMembershipVoucherSiteRestricted) {
      if (voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] === _.includes(voucher.membershipVoucher.siteIds, this.configuration.currentSite.id)) return false
    }

    return true
  }

  isShowingMatch (showing) {
    const otherSiteShowingIds = _.without(this.showingIds, this.originalData.siteShowingIds)
    const finalShowingIds = _.union(otherSiteShowingIds, this.siteShowingIds)
    if (this.excludeShowings || !_.isEmpty(finalShowingIds)) {
      if (!!this.excludeShowings === _.includes(finalShowingIds, showing.id)) return false
    }

    const otherSiteMovieIds = _.without(this.movieIds, this.originalData.siteMovieIds)
    const finalMovieIds = _.union(otherSiteMovieIds, this.siteMovieIds)
    if (this.excludeMovies || !_.isEmpty(finalMovieIds)) {
      if (!!this.excludeMovies === _.includes(finalMovieIds, showing.movie.id)) return false
    }

    if (this.excludeTitleClasses || !_.isEmpty(this.titleClassIds)) {
      if (!!this.excludeTitleClasses === _.includes(this.titleClassIds, showing.movie.titleClassId)) return false
    }

    return true
  }

  isDateMatch (orderItem, voucher = null) {
    let showingOrActivitySessionTime
    let showingOrActivitySessionDayNumber

    if (orderItem.type === ORDER_ITEM_TYPES.TICKET) {
      showingOrActivitySessionTime = orderItem.localShowing?.siteTime
      showingOrActivitySessionDayNumber = orderItem.localShowing?.dayNumber
    } else if (orderItem.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
      showingOrActivitySessionTime = orderItem.localActivityTicket?.activitySession.siteStartTime
      showingOrActivitySessionDayNumber = orderItem.localActivityTicket?.activitySession.dayNumber
    }

    if (this.conditions[VOUCHER_TYPE_CONDITIONS.DATES]) {
      const time = showingOrActivitySessionTime ? new IsoDate(showingOrActivitySessionTime) : new IsoDate()
      const dateString = time.format('MM-DD')

      if (this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATES] === _.includes(this.conditionsRequirements[CONDITION_REQUIREMENTS.DATES], dateString)) return false
    }

    const dayNumber = showingOrActivitySessionDayNumber || new Date().getDay()
    if (this.conditions[VOUCHER_TYPE_CONDITIONS.DAY_OF_WEEK]) {
      if (this.isDayOfWeekMatch(
        dayNumber,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK])
      ) return false
    }
    if (voucher?.membershipVoucher && voucher.membershipVoucher.conditions[VOUCHER_TYPE_CONDITIONS.DAY_OF_WEEK]) {
      if (this.isDayOfWeekMatch(
        dayNumber,
        voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK],
        voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK])
      ) return false
    }

    const time = showingOrActivitySessionTime ? new Date(showingOrActivitySessionTime) : new Date()
    const hour = time.getHours()
    const minute = time.getMinutes()
    if (this.conditions[VOUCHER_TYPE_CONDITIONS.TIME_OF_DAY]) {
      if (this.isTimeOfDayMatch(
        hour,
        minute,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.TIME_OF_DAY_START],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.TIME_OF_DAY_END],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_TIME_OF_DAY])
      ) return false
    }

    const dateTime = showingOrActivitySessionTime ? new Date(showingOrActivitySessionTime).getTime() : new Date().getTime()
    if (this.conditions[VOUCHER_TYPE_CONDITIONS.DATE_RANGE]) {
      if (this.isDateRangeMatch(
        dateTime,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE])
      ) return false
    }
    if (voucher?.membershipVoucher && voucher.membershipVoucher.conditions[VOUCHER_TYPE_CONDITIONS.DATE_RANGE]) {
      if (this.isDateRangeMatch(
        dateTime,
        voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START],
        voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END],
        voucher.membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE])
      ) return false
    }

    return true
  }

  isDayOfWeekMatch (currentDay, dayOfWeek, excludeDayOfWeek) {
    const isDayOfWeekMatch = _.includes(dayOfWeek, currentDay)
    return excludeDayOfWeek === isDayOfWeekMatch
  }

  isDateRangeMatch (currentTime, dateRangeStart, dateRangeEnd, excludeDateRange) {
    const startDate = new Date(dateRangeStart).getTime()
    const isAfterStartDate = currentTime > startDate

    let isBeforeEndDate = null
    if (dateRangeEnd) {
      const endDate = addToDate(new Date(dateRangeEnd), { days: 1 }).getTime()
      isBeforeEndDate = currentTime < endDate
    }

    const includesDateRange = isAfterStartDate && (_.isNil(isBeforeEndDate) || isBeforeEndDate)

    return excludeDateRange === includesDateRange
  }

  isTimeOfDayMatch (currentHour, currentMinute, timeOfDayStart, timeOfDayEnd, excludeTimeOfDay) {
    const [startHour, startMinute] = timeOfDayStart.split(':')
    const isAfterStartTime = currentHour > startHour || (currentHour === startHour && currentMinute > startMinute)

    let isBeforeEndTime = null
    if (timeOfDayEnd) {
      const [endHour, endMinute] = timeOfDayEnd.split(':')
      isBeforeEndTime = currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)
    }

    const includesTimeOfDay = isAfterStartTime && (_.isNil(isBeforeEndTime) || isBeforeEndTime)

    return excludeTimeOfDay === includesTimeOfDay
  }

  isActivitySessionMatch (activitySession) {
    const otherSiteActivitySessionIds = _.without(this.activitySessionIds, this.originalData.siteActivitySessionIds)
    const finalActivitySessionIds = _.union(otherSiteActivitySessionIds, this.siteActivitySessionIds)
    if (this.excludeActivitySessions || !_.isEmpty(finalActivitySessionIds)) {
      if (!!this.excludeActivitySessions === _.includes(finalActivitySessionIds, activitySession.id)) return false
    }

    const otherSiteActivityIds = _.without(this.activityIds, this.originalData.siteActivityIds)
    const finalActivityIds = _.union(otherSiteActivityIds, this.siteActivityIds)
    if (this.excludeActivities || !_.isEmpty(finalActivityIds)) {
      if (!!this.excludeActivities === _.includes(finalActivityIds, activitySession.activity.id)) return false
    }

    return true
  }

  isItemOrderItem (itemOrderItem) {
    if (this.excludeItemClasses || !_.isEmpty(this.itemClassIds)) {
      if (!!this.excludeItemClasses === _.includes(this.itemClassIds, itemOrderItem.item.itemClassId)) return false
    }

    const otherSiteItemIds = _.without(this.itemIds, this.originalData.siteItemIds)
    const finalItemIds = _.union(otherSiteItemIds, this.siteItemIds)

    if (this.excludeItems || !_.isEmpty(finalItemIds)) {
      if (!!this.excludeItems === _.includes(finalItemIds, itemOrderItem.item.id)) return false
    }

    const otherSiteItemVariationIds = _.without(this.itemVariationIds, this.originalData.siteItemVariationIds)
    const finalItemVariationIds = _.union(otherSiteItemVariationIds, this.siteItemVariationIds)
    if (this.excludeItemVariations || !_.isEmpty(finalItemVariationIds)) {
      if (!!this.excludeItemVariations === _.includes(finalItemVariationIds, itemOrderItem.itemVariation.id)) return false
    }

    return true
  }

  isItem (item) {
    // Specific voucher types have one necessary item variation
    if (!_.isNil(this.itemVariation?.id)) {
      return this.itemVariation.id !== item.itemVariationId
    }

    if (this.excludeItemClasses || this.itemClassIds?.length) {
      if (!!this.excludeItemClasses === _.includes(this.itemClassIds, item.itemClassId)) return false
    }

    if (this.excludeItems || !_.isEmpty(this.itemIds)) {
      if (!!this.excludeItems === _.includes(this.itemIds, item.id)) return false
    }

    if (this.excludeItemVariations || !_.isEmpty(this.itemVariationIds)) {
      if (!!this.excludeItemVariations === _.includes(this.itemVariationIds, item.itemVariationId)) return false
    }

    return true
  }

  validateCardUid (uid) {
    const strippedUid = this.strippedUid(uid)

    // If we do not force card uid pattern match auto-pass
    if (!this.barcodeOptions.require_card_uid_prefix_match) return true

    // If card uid does not start with given prefix fail
    if (!strippedUid.startsWith(this.barcodeOptions.card_uid_prefix)) return false

    // If card uid does not contain given incrementor fail
    if (!/^\d+$/.test(strippedUid.slice(this.barcodeOptions.card_uid_prefix.length, this.barcodeOptions.card_uid_prefix.length + this.barcodeOptions.card_uid_increment_length))) return false

    return true
  }

  strippedUid (uid) {
    let strippedUid = uid

    if (this.configuration?.currentCircuit.cardPrefixesToStrip.length) {
      const prefixStripper = new RegExp(`${_.map(this.configuration?.currentCircuit.cardPrefixesToStrip, cardPrefixToStrip => _.escapeRegExp(cardPrefixToStrip)).join('|')}`, 'g')
      strippedUid = strippedUid.replace(prefixStripper, '')
    }

    if (this.configuration?.currentCircuit.cardSuffixesToStrip.length) {
      const suffixStripper = new RegExp(`${_.map(this.configuration?.currentCircuit.cardSuffixesToStrip, cardSuffixToStrip => _.escapeRegExp(cardSuffixToStrip)).join('|')}`, 'g')
      strippedUid = strippedUid.replace(suffixStripper, '')
    }

    return strippedUid
  }

  isRedeemableForRewards (points = null) {
    return this.isReward &&
    (points === null || this.pointsCost <= points) &&
    !this.hasMinValue && (
      (this.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM && this.itemVariationId) ||
      this.type === VOUCHER_TYPE_TYPES.ITEM
    )
  }

  showOnMembershipPage () {
    return this.isReward
  }
}
